import React, { useState, useEffect } from "react";
import { useTheme } from "@/context/ThemeContext";
import leadGenLogo from "@/assets/images/lead-generation-logo.png";
import {
  Phone,
  ArrowRight,
  BarChart3,
  TrendingUp,
  Target,
  Zap,
  Users,
  ChevronDown,
  DollarSign,
  Sparkles,
  LineChart,
  PhoneIncoming,
  MapPin,
  Printer,
  Palette,
  Database,
  UserCheck,
  Lightbulb,
  ShieldCheck,
  Clock,
  Mail,
} from "lucide-react";

const LeadGeneration = () => {
  const [customerCount, setCustomerCount] = useState(1000);
  const [avgInvoice, setAvgInvoice] = useState(500);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [animatedReturn, setAnimatedReturn] = useState(0);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // CertainPath brand colors - burgundy/maroon gradient theme
  const colors = {
    primary: isDark ? "#CC4059" : "rgb(109, 21, 51)",
    primaryDark: isDark ? "#B21E34" : "rgb(78, 15, 36)",
    primaryLight: isDark ? "rgba(217, 76, 101, 0.1)" : "rgb(109, 21, 51, 0.1)",
    primaryLightSolid: isDark ? "rgba(217, 76, 101, 0.15)" : "#F9E8ED",
    gradient:
      "bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)]",
  };

  // Performance benchmarks - ~3 calls per 1,000 postcards mailed
  const callsPer1K = 3;
  const expectedCalls = Math.round((customerCount / 1000) * callsPer1K);
  const expectedRevenue = expectedCalls * avgInvoice;
  const leadGenFormUrl = "https://info.mycertainpath.com/leadgeneration";

  // Animate return number when it changes
  useEffect(() => {
    const targetReturn = expectedRevenue;
    const duration = 800;
    const steps = 30;
    const stepTime = duration / steps;

    let currentStep = 0;
    const startReturn = animatedReturn;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedReturn(
        Math.round(startReturn + (targetReturn - startReturn) * easeOut),
      );

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedReturn(targetReturn);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [customerCount, avgInvoice]);

  const faqs: { question: string; answer: string[] }[] = [
    {
      question: "What response rates can I expect?",
      answer: [
        "Across more than 50 million pieces mailed, our campaigns average approximately 3\u20134 calls per 1,000 pieces, with many offers performing higher depending on service type, seasonality, and market conditions.",
        "High-performing campaigns can exceed 5+ calls per 1,000 when targeting, timing, and offer alignment are strong.",
        "Our structured campaign approach is designed to build consistent demand over time\u2014not one-time spikes\u2014and we continuously optimize targeting to improve performance.",
      ],
    },
    {
      question: "How do you identify the right homeowners?",
      answer: [
        "We use 200+ data points\u2014including home value, age, income, and service-area insights\u2014combined with your own customer data to build a high-propensity audience. This isn\u2019t spray-and-pray marketing. It\u2019s precision targeting.",
      ],
    },
    {
      question: "How do you measure results?",
      answer: [
        "We use match-back reporting and call analysis to connect mailed households to actual revenue. This provides clear visibility into the lift direct mail generates across your overall marketing mix.",
      ],
    },
    {
      question:
        "What makes CertainPath Lead Generation different from other mail vendors?",
      answer: [
        "CertainPath Lead Generation is not a print vendor\u2014it\u2019s a structured growth system.",
        "We combine 15+ years of campaign performance data, precision targeting, match-back revenue reporting, and ongoing optimization guidance. Our campaigns are built to generate measurable revenue, not just mail volume.",
        "For CertainPath members, performance insights are also visible, including MEER in the HUB, allowing you to benchmark results and understand your true market opportunity.",
      ],
    },
    {
      question: "How do you access our customer and invoice data?",
      answer: [
        "We securely integrate with leading field service and accounting platforms such as ServiceTitan and QuickBooks. These integrations allow us to analyze transaction data, perform match-back reporting, and generate performance insights within MEER.",
        "If you use a different system, we can ingest exported data securely to ensure accurate analysis and benchmarking.",
        "All data is handled securely and used solely for campaign optimization and performance reporting.",
      ],
    },
    {
      question: "How is pricing determined?",
      answer: [
        "Your investment is based on the weekly volume required to generate results in your specific market. We analyze your geography and growth goals to recommend the right campaign structure and volume.",
        "For our best-value 54-week postcard campaign (10% savings), weekly pricing is: 0\u20132,500 pieces at $1,463/week, 2,500\u20133,500 at $2,048/week, 3,500\u20134,500 at $2,633/week, 4,500\u20135,500 at $3,218/week, and 5,500+ at custom pricing. All pricing is all-inclusive.",
        "Direct mail performance compounds over time. Sustained campaigns build brand recognition, improve response consistency, and create predictable lead flow. Commit longer and save more: 18 weeks at standard pricing, 36 weeks saves 5%, and 54 weeks saves 10%.",
        "The longer the commitment, the greater the efficiency and savings.",
      ],
    },
    {
      question: "Will this conflict with my existing marketing?",
      answer: [
        "No. Direct mail is designed to strengthen your overall marketing mix\u2014not compete with it.",
        "In many cases, match-back reporting shows that direct mail increases overall revenue lift, even when customers attribute the job to another channel. Our goal is to help you understand how mail supports and amplifies your broader marketing investment.",
      ],
    },
    {
      question: "What kind of commitment is required?",
      answer: [
        "Campaigns begin at 18 weeks because consistency drives performance. Longer commitments improve efficiency and unlock savings\u2014without multi-year contracts or long-term lock-ins.",
        "18 weeks is standard pricing, 36 weeks saves 5%, and 54 weeks saves 10%. The longer the commitment, the greater the efficiency and savings.",
      ],
    },
    {
      question: "Do you fully customize each mailer?",
      answer: [
        "We use a curated library of proven direct mail creatives that have been refined over millions of pieces mailed. These formats are designed for response\u2014clear offers, strong calls to action, and layout that consistently generate calls.",
        "Contractors personalize key variable elements such as company name and logo, offer details, contact information, and service focus.",
        "This approach protects performance integrity while still allowing your brand and offer to shine.",
      ],
    },
    {
      question: "How quickly can we get started?",
      answer: [
        "Most contractors launch within 1\u20132 weeks. We handle audience development, targeting, creative selection, production, and mailing\u2014so you can focus on running your business.",
      ],
    },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gray-50 dark:bg-slate-950">
      {/* Subtle Animated Background - Floating Blobs */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div
          className="absolute w-96 h-96 rounded-full opacity-[0.03] blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgb(109, 21, 51) 0%, transparent 70%)",
            animation: "float1 25s ease-in-out infinite",
            top: "10%",
            left: "5%",
          }}
        />
        <div
          className="absolute w-80 h-80 rounded-full opacity-[0.03] blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgb(78, 15, 36) 0%, transparent 70%)",
            animation: "float2 30s ease-in-out infinite",
            top: "60%",
            right: "10%",
          }}
        />
        <div
          className="absolute w-72 h-72 rounded-full opacity-[0.05] blur-3xl"
          style={{
            background: "radial-gradient(circle, #374151 0%, transparent 70%)",
            animation: "float3 28s ease-in-out infinite",
            bottom: "20%",
            left: "15%",
          }}
        />
      </div>
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 30px) scale(0.9); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 40px) scale(1.15); }
          66% { transform: translate(30px, -30px) scale(0.85); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, 30px) scale(0.9); }
          66% { transform: translate(-30px, -40px) scale(1.1); }
        }
        @keyframes heroGradient {
          0%, 100% {
            background: radial-gradient(ellipse at 30% 20%, rgba(109, 21, 51, 0.6) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(78, 15, 36, 0.6) 0%, transparent 50%);
          }
          50% {
            background: radial-gradient(ellipse at 70% 30%, rgba(109, 21, 51, 0.6) 0%, transparent 50%), radial-gradient(ellipse at 30% 70%, rgba(78, 15, 36, 0.6) 0%, transparent 50%);
          }
        }
        @keyframes shimmer {
          0% { background-position: -200% -200%; }
          100% { background-position: 200% 200%; }
        }
      `}</style>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)]">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse at 30% 20%, rgba(109, 21, 51, 0.6) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(78, 15, 36, 0.6) 0%, transparent 50%)",
            animation: "heroGradient 15s ease-in-out infinite",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            background:
              "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)",
            backgroundSize: "200% 200%",
            animation: "shimmer 8s ease-in-out infinite",
          }}
        />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16 relative z-10">
          <div className="text-center">
            <img
              alt="CertainPath Lead Generation"
              className="mx-auto mb-6"
              src={leadGenLogo}
              style={{ maxHeight: "80px", width: "auto" }}
            />
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-6 bg-yellow-400 text-gray-900 shadow-lg">
              <Sparkles className="w-4 h-4" />
              4% Partner Network Rebate
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight tracking-wide">
              Drive Predictable,
              <br />
              <span className="font-normal">High-Intent Leads</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed font-light">
              Direct mail powered by data, analytics, and continuous
              optimization—designed to generate measurable demand from{" "}
              <span className="font-medium text-white">
                high-value homeowners
              </span>{" "}
              in your market.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <a
                className="group px-6 py-3 rounded-md font-semibold text-sm transition-all duration-200 inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100"
                href={leadGenFormUrl}
                rel="noopener noreferrer"
                style={{ color: colors.primary }}
                target="_blank"
              >
                <Phone className="w-4 h-4" />
                Talk to Our Team
              </a>
              <a
                className="group px-6 py-3 rounded-md font-semibold text-sm transition-all duration-200 inline-flex items-center justify-center gap-2 border border-white/30 text-white hover:bg-white/10"
                href="#how-it-works"
              >
                See How It Works
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <p className="text-base text-white/80 font-medium mt-10 mb-6">
              Structured Direct Mail Campaigns Built for Sustainable Growth
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                  <Target className="w-5 h-5 text-white/80" />
                </div>
                <p className="text-sm text-gray-300 font-light leading-snug">
                  Precision targeting focused on high-value homeowners
                </p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-5 h-5 text-white/80" />
                </div>
                <p className="text-sm text-gray-300 font-light leading-snug">
                  18-, 36-, or 54-week campaign options designed to build
                  momentum
                </p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-5 h-5 text-white/80" />
                </div>
                <p className="text-sm text-gray-300 font-light leading-snug">
                  Ongoing performance visibility through MEER in the HUB
                </p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-5 h-5 text-white/80" />
                </div>
                <p className="text-sm text-gray-300 font-light leading-snug">
                  A disciplined approach that keeps your pipeline full
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Contractors Choose Us */}
      <div className="max-w-6xl mx-auto px-6 pt-20 mb-32">
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
            style={{
              backgroundColor: colors.primaryLightSolid,
              color: colors.primary,
            }}
          >
            <ShieldCheck className="w-4 h-4" />
            Trusted by 500+ Contractors
          </div>
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
            Why Contractors Choose CertainPath Lead Generation
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Everything you need to generate consistent demand and growth with
            confidence.
          </p>
        </div>

        {/* Top 3 Featured Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            {
              icon: <LineChart className="w-7 h-7" />,
              title: "Market Benchmarking",
              description:
                "Benchmark your campaign performance against competitors through MEER insights in the HUB.",
              highlight: "Competitive performance visibility",
              color: "#7c3aed",
            },
            {
              icon: <TrendingUp className="w-7 h-7" />,
              title: "Revenue Match-Back Reporting",
              description:
                "See which households received mail and later generated revenue. Our match-back reporting shows the measurable lift direct mail creates across your marketing mix.",
              highlight: "Revenue visibility\u2014without guesswork",
              color: "#059669",
            },
            {
              icon: <PhoneIncoming className="w-7 h-7" />,
              title: "AI-Enhanced Call Insights",
              description:
                "Call recordings are analyzed to identify booking opportunities and improve conversion performance.",
              highlight: "Conversion optimization insights",
              color: "#0891b2",
            },
          ].map((benefit, index) => (
            <div
              className="group bg-white dark:bg-slate-800 rounded-2xl p-8 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-lg transition-all duration-300 relative overflow-hidden"
              key={index}
            >
              <div
                className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ backgroundColor: benefit.color }}
              />
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                style={{
                  backgroundColor: `${benefit.color}15`,
                  color: benefit.color,
                }}
              >
                {benefit.icon}
              </div>
              <h3 className="font-bold text-xl text-gray-900 dark:text-white mb-3">
                {benefit.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
                {benefit.description}
              </p>
              <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
                <span
                  className="text-sm font-semibold"
                  style={{ color: benefit.color }}
                >
                  {benefit.highlight}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              icon: <MapPin className="w-5 h-5" />,
              title: "Precision Targeting",
              description:
                "Reach high-value homeowners using income, home value, home age, and other key indicators, not just zip codes.",
              highlight: "200+ targeting data points",
            },
            {
              icon: <Printer className="w-5 h-5" />,
              title: "Proven Mail Formats",
              description:
                "Our performance-tested postcard and letter formats are built for response\u2014not decoration.",
              highlight: "Designed for response",
            },
            {
              icon: <Palette className="w-5 h-5" />,
              title: "Seasonal Creative Library",
              description:
                "Access a curated library of high-performing creative tailored to season and service type.",
              highlight: "Campaign-ready designs",
            },
            {
              icon: <Database className="w-5 h-5" />,
              title: "Smart Data Hygiene",
              description:
                "We continuously clean and suppress lists to reduce waste and protect your investment.",
              highlight: "Minimize undeliverable mail",
            },
            {
              icon: <UserCheck className="w-5 h-5" />,
              title: "Dedicated Campaign Manager",
              description:
                "One point of contact who manages targeting, production, and performance oversight.",
              highlight: "Direct support",
            },
            {
              icon: <Lightbulb className="w-5 h-5" />,
              title: "Strategic Optimization Guidance",
              description:
                "Ongoing recommendations on timing, volume, offers, and targeting based on campaign data.",
              highlight: "15+ years of campaign refinement",
            },
            {
              icon: <Zap className="w-5 h-5" />,
              title: "Fast Turnaround",
              description: "From approval to mailbox in as little as one week.",
              highlight: "~7 days to deploy",
            },
            {
              icon: <Clock className="w-5 h-5" />,
              title: "Structured Growth Commitments",
              description:
                "Campaigns begin at 18 weeks, with longer durations designed to build momentum and improve efficiency.",
              highlight: "Built for sustained demand",
            },
            {
              icon: <Mail className="w-5 h-5" />,
              title: "For more than 15 years",
              description:
                "We\u2019ve helped contractors generate measurable revenue through disciplined, data-driven direct mail campaigns.",
              highlight: "Proven track record",
            },
          ].map((benefit, index) => (
            <div
              className="group bg-white dark:bg-slate-800 rounded-xl p-5 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-md transition-all duration-200"
              key={index}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: colors.primaryLightSolid,
                    color: colors.primary,
                  }}
                >
                  {benefit.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-2">
                    {benefit.description}
                  </p>
                  <span
                    className="inline-block text-xs font-semibold px-2 py-1 rounded-full"
                    style={{
                      backgroundColor: colors.primaryLightSolid,
                      color: colors.primary,
                    }}
                  >
                    {benefit.highlight}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6">
        {/* How It Works */}
        <div className="mb-32" id="how-it-works">
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 text-center tracking-tight">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-20 text-center max-w-2xl mx-auto font-light">
            A disciplined system that turns marketing into measurable revenue.
          </p>

          <div className="relative">
            <div
              className="hidden md:block absolute top-8 left-0 right-0 h-1 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700"
              style={{ top: "60px" }}
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
              {[
                {
                  title: "Business and Market Analysis",
                  description:
                    "We analyze your historical data and benchmark your performance to identify missed revenue opportunities and define your true market potential.",
                  tagline: "Know where to market. Know what to expect.",
                  icon: <BarChart3 className="w-8 h-8" />,
                },
                {
                  title: "Smarter Targeting",
                  description:
                    "We merge your customer data with high-propensity homeowner data to build a clean, optimized audience of households most likely to convert.",
                  tagline: "Target the right homes\u2014not just zip codes.",
                  icon: <Target className="w-8 h-8" />,
                },
                {
                  title: "Structured Direct Mail Execution",
                  description:
                    "We deploy targeted postcards and letters weekly as part of an 18-, 36-, or 54-week campaign\u2014using proven messaging designed to generate consistent call volume.",
                  tagline: "Disciplined campaigns. Consistent demand.",
                  icon: <Mail className="w-8 h-8" />,
                },
                {
                  title: "Performance Tracking and Optimization",
                  description:
                    "Through match-back reporting and call analysis, we measure campaign impact and continuously refine targeting to improve results over time.",
                  tagline: "Measure lift. Improve conversion. Grow revenue.",
                  icon: <TrendingUp className="w-8 h-8" />,
                },
              ].map((step, index) => (
                <div className="relative" key={index}>
                  <div className="bg-white dark:bg-slate-800 rounded-2xl p-10 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 transition-all duration-200">
                    <div
                      className="flex justify-center mb-6"
                      style={{ color: colors.primary }}
                    >
                      {step.icon}
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-3 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed text-sm mb-4">
                      {step.description}
                    </p>
                    <p
                      className="text-center text-sm font-semibold italic"
                      style={{ color: colors.primary }}
                    >
                      {step.tagline}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="mb-32">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{
                backgroundColor: colors.primaryLightSolid,
                color: colors.primary,
              }}
            >
              <DollarSign className="w-4 h-4" />
              Transparent, Data-Driven Pricing
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
              Built Around Your Market&mdash;Not Guesswork
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              We analyze your geography, household density, competition, and
              growth goals to determine the optimal weekly volume designed to
              generate consistent, high-intent leads&mdash;without overspending.
            </p>
            <p className="text-base text-gray-500 dark:text-gray-500 max-w-2xl mx-auto mt-4">
              Your weekly investment reflects the volume required to generate
              results in your specific market.
            </p>
          </div>

          {/* Pricing & Commitment */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-8 md:p-10 max-w-4xl mx-auto">
            {/* 54-Week Sample Pricing */}
            <div className="text-center mb-8">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4"
                style={{
                  backgroundColor: colors.primaryLightSolid,
                  color: colors.primary,
                }}
              >
                Best ROI &mdash; 10% Savings
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                54-Week Postcard Campaign
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Weekly investment, all-inclusive
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
              {[
                { volume: "0\u20132,500", price: "$1,463" },
                { volume: "2,500\u20133,500", price: "$2,048" },
                { volume: "3,500\u20134,500", price: "$2,633" },
                { volume: "4,500\u20135,500", price: "$3,218" },
                { volume: "5,500+", price: "Custom" },
              ].map((tier, index) => (
                <div
                  className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700 p-4 text-center"
                  key={index}
                >
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-2">
                    Weekly Volume
                  </div>
                  <div
                    className="text-lg font-bold mb-3"
                    style={{ color: colors.primary }}
                  >
                    {tier.volume}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1">
                    Per Week
                  </div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                    {tier.price}
                  </div>
                  {tier.price !== "Custom" && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      /week
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-slate-700 my-10" />

            {/* Commit Longer. Save More. */}
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
              Commit Longer. Save More.
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-8 max-w-2xl mx-auto leading-relaxed">
              Direct mail performance compounds over time. Sustained campaigns
              build brand recognition, improve response consistency, and create
              predictable lead flow.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 max-w-3xl mx-auto">
              <div className="rounded-xl p-5 text-center border-2 border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700">
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  18 Weeks
                </div>
                <div className="text-lg font-semibold text-gray-500 dark:text-gray-400">
                  Standard pricing
                </div>
              </div>
              <div className="rounded-xl p-5 text-center border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  36 Weeks
                </div>
                <div className="text-lg font-semibold text-green-700 dark:text-green-400">
                  Save 5%
                </div>
              </div>
              <div className="rounded-xl p-5 text-center border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  54 Weeks
                </div>
                <div className="text-lg font-semibold text-green-700 dark:text-green-400">
                  Save 10%
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 text-center">
              The longer the commitment, the greater the efficiency and savings.
            </p>
          </div>
        </div>

        {/* ROI Estimator */}
        <div className="mb-32">
          <div className="bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)] rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

            <div className="relative z-10">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm font-medium mb-6 backdrop-blur-sm">
                  <Sparkles className="w-4 h-4" />
                  Revenue Estimator
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                  Estimate Your New Customer Revenue
                </h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  Based on our performance benchmark of ~{callsPer1K} calls per
                  1,000 postcards mailed
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-5 space-y-6">
                  {/* Postcards to Mail Slider */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-white font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-pink-300" />
                        Postcards to Mail
                      </label>
                      <span className="text-3xl font-bold text-white">
                        {customerCount.toLocaleString()}
                      </span>
                    </div>
                    <input
                      className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                      max="10000"
                      min="100"
                      onChange={(e) => setCustomerCount(Number(e.target.value))}
                      step="100"
                      style={{
                        background: `linear-gradient(to right, rgb(109, 21, 51) 0%, rgb(109, 21, 51) ${((customerCount - 100) / 9900) * 100}%, rgba(255,255,255,0.2) ${((customerCount - 100) / 9900) * 100}%, rgba(255,255,255,0.2) 100%)`,
                      }}
                      type="range"
                      value={customerCount}
                    />
                    <div className="flex justify-between text-xs text-white/50 mt-2">
                      <span>100</span>
                      <span>10,000</span>
                    </div>
                  </div>

                  {/* Average Invoice Amount */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-white font-semibold flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        Average Invoice
                      </label>
                      <span className="text-3xl font-bold text-white">
                        ${avgInvoice.toLocaleString()}
                      </span>
                    </div>
                    <input
                      className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                      max="20000"
                      min="100"
                      onChange={(e) => setAvgInvoice(Number(e.target.value))}
                      step="50"
                      style={{
                        background: `linear-gradient(to right, #22c55e 0%, #22c55e ${((avgInvoice - 100) / 19900) * 100}%, rgba(255,255,255,0.2) ${((avgInvoice - 100) / 19900) * 100}%, rgba(255,255,255,0.2) 100%)`,
                      }}
                      type="range"
                      value={avgInvoice}
                    />
                    <div className="flex justify-between text-xs text-white/50 mt-2">
                      <span>$100</span>
                      <span>$20,000</span>
                    </div>
                    <p className="text-xs text-white/40 mt-3 text-center">
                      What&rsquo;s your typical ticket value?
                    </p>
                  </div>

                  {/* Performance Benchmark */}
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                        <PhoneIncoming className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <div className="text-white/60 text-sm">
                          Performance Benchmark
                        </div>
                        <div className="text-xl font-bold text-white">
                          ~{callsPer1K} calls per 1,000 postcards
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-white/40 mt-3">
                      Based on 50M+ pieces mailed across our contractor network
                    </p>
                  </div>
                </div>

                {/* Right Side - Revenue Results */}
                <div className="lg:col-span-7">
                  <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 h-full flex flex-col justify-center">
                    <div className="text-center mb-10">
                      <div className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-4">
                        Expected New Customer Revenue
                      </div>
                      <div
                        className="text-6xl md:text-7xl font-bold mb-6"
                        style={{ color: colors.primary }}
                      >
                        ${animatedReturn.toLocaleString()}
                      </div>
                      <div className="text-2xl text-gray-700 dark:text-gray-300 font-medium">
                        from{" "}
                        <span
                          className="font-bold"
                          style={{ color: colors.primary }}
                        >
                          {expectedCalls} expected calls
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-6 mb-8">
                      <div className="flex items-center justify-center gap-4 text-center">
                        <div className="flex-1">
                          <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            Postcards Mailed
                          </div>
                          <div className="text-3xl font-bold text-gray-900 dark:text-white">
                            {customerCount.toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center justify-center">
                          <ArrowRight className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <div
                            className="text-sm mb-2"
                            style={{ color: colors.primary }}
                          >
                            Expected Calls
                          </div>
                          <div
                            className="text-3xl font-bold"
                            style={{ color: colors.primary }}
                          >
                            {expectedCalls}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-600 text-center">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          ~{callsPer1K} calls per 1,000 postcards ×{" "}
                          <span className="font-semibold">
                            {customerCount.toLocaleString()} postcards
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          ={" "}
                          <span className="font-semibold">
                            {expectedCalls} new customers
                          </span>{" "}
                          ×{" "}
                          <span className="font-semibold">
                            ${avgInvoice.toLocaleString()}
                          </span>{" "}
                          avg. invoice
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 mb-8">
                      <div className="flex items-center justify-center gap-6">
                        <div className="text-center">
                          <div className="text-sm text-green-700 dark:text-green-400 font-medium mb-1">
                            Estimated Revenue
                          </div>
                          <div className="text-3xl font-bold text-green-700 dark:text-green-400">
                            ${expectedRevenue.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    <a
                      className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90 hover:scale-[1.02]"
                      href={leadGenFormUrl}
                      rel="noopener noreferrer"
                      style={{ backgroundColor: colors.primary }}
                      target="_blank"
                    >
                      <Phone className="w-5 h-5" />
                      Get Your Personalized Analysis
                      <ArrowRight className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-32">
          <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-6 text-center tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-16 text-center max-w-2xl mx-auto font-light">
            Everything you need to know about CertainPath Lead Generation
          </p>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
                key={index}
              >
                <button
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  type="button"
                >
                  <span className="font-bold text-lg text-gray-900 dark:text-white pr-8">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`w-6 h-6 flex-shrink-0 transition-transform duration-300 ${openFaq === index ? "rotate-180" : ""}`}
                    style={{ color: colors.primary }}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? "max-h-[800px]" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6 space-y-3">
                    {faq.answer.map((paragraph, pIndex) => (
                      <p
                        className="text-gray-600 dark:text-gray-400 leading-relaxed"
                        key={pIndex}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div
          className="rounded-3xl p-16 text-center mb-12 relative overflow-hidden bg-gradient-to-br from-gray-900 via-[rgb(109,21,51)] to-[rgb(78,15,36)]"
          id="contact"
        >
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          <div className="relative">
            <Users className="w-16 h-16 text-white mx-auto mb-6 opacity-70" />
            <h2 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-wide">
              Know Your Market. Win More Customers.
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
              Request your complimentary market analysis and see how much
              untapped revenue exists in your service area.
            </p>

            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <a
                className="group bg-white px-10 py-5 rounded-md font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center gap-3 hover:bg-gray-100"
                href={leadGenFormUrl}
                rel="noopener noreferrer"
                style={{ color: colors.primary }}
                target="_blank"
              >
                <Phone className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                Talk to Our Team
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadGeneration;
